// organize-imports-ignore — import order here is load-bearing.
/** App-side bindings for Evidence answer capabilities, partner config, and source actions. */

import { viewingSourceAtom } from '@/app-shell/sourceViewerAtoms';
import {
  getEvidenceMdComponents,
  mdComponents,
} from '@/components/markdown/markdown-components';
import { ChatMessageContext } from '@/context/ChatMessageContext';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { useIsCpdQueryEnabled } from '@/hooks/useIsCpdSurfaceEnabled';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import { isAuthenticatedSourceUrl } from '@/utils/authenticatedSourceUrl';
import { triggerBrowserPrint } from '@/utils/browserPrint';
import { PUBLIC_ROUTES } from '@/utils/publicRoutes';
import {
  CitationOcrQualityTag,
  documentPreviewAtom,
  SourceCardActions,
  type EvidenceAnswerPorts,
} from '@heidi/evidence';
import { getPartnerFigureUrl, markSourceReadByUrl } from '@heidi/evidence/data';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { getPartnerConfig } from '../partner-content/integrationPartners';

export type EvidenceSourceOpeners = Pick<
  EvidenceAnswerPorts,
  'openInViewer' | 'openLibraryDocument' | 'openInNewTab' | 'isAuthenticatedUrl'
>;

/**
 * The members that open a source and report the click: what a source list
 * needs, without the answer's analytics subscriptions. Bind it once where the
 * list's owner is composed.
 */
export const useEvidenceSourceOpeners = (): EvidenceSourceOpeners => {
  const setViewingSource = useSetAtom(viewingSourceAtom);
  const setPreviewState = useSetAtom(documentPreviewAtom);
  return useMemo<EvidenceSourceOpeners>(
    () => ({
      openInViewer: (source) => setViewingSource(source),
      openLibraryDocument: (documentId) => setPreviewState({ isOpen: true, documentId }),
      openInNewTab: (url) => window.open(url, '_blank', 'noopener,noreferrer'),
      isAuthenticatedUrl: isAuthenticatedSourceUrl,
    }),
    [setViewingSource, setPreviewState]
  );
};

/** Builds the bundle. Memoised, which is why `isPublicRoute` is a thunk. */
export const useEvidenceAnswerPortsValue = (): EvidenceAnswerPorts => {
  const router = useRouter();
  const openers = useEvidenceSourceOpeners();
  const { baseProps } = useEvidenceAnalytics();
  const isCpdQueryEnabled = useIsCpdQueryEnabled();

  return useMemo<EvidenceAnswerPorts>(() => {
    return {
      ...openers,
      isPublicRoute: () =>
        PUBLIC_ROUTES.some((route) => router.pathname.startsWith(route)),
      ocrQualityTag: CitationOcrQualityTag,
      getPartnerConfig,
      sourceActionsComponent: SourceCardActions,
      isCpdQueryEnabled,
      onNavigate: (href) => void router.push(href),
      analyticsBaseProps: baseProps,
      markSourceRead: (url, title) => void markSourceReadByUrl(url, title),
      printDocument: (url, context) => void triggerBrowserPrint(url, context),
      resolvePartnerFigureUrl: getPartnerFigureUrl,
      useKindeRegion: useIsUKOrEU,
      getEvidenceMdComponents,
      // The legacy map's anchor props are wider than Streamdown's component
      // type. The reference stays `mdComponents`; only the annotation widens.
      legacyMdComponents:
        mdComponents as unknown as EvidenceAnswerPorts['legacyMdComponents'],
      messageContext: ChatMessageContext,
    };
  }, [router, openers, baseProps, isCpdQueryEnabled]);
};
